<script setup lang="ts">
import { computed, ref } from "vue";
import { useQuery, useQueryClient } from "@tanstack/vue-query";
import {
  UiButton,
  UiDialog,
  UiIcon,
  UiIconButton,
  UiSelect,
  UiText,
  useToaster,
} from "@shopexperts/shared";
import type { AdminExpertDetail } from "@shopexperts/legacy-api";

import ExpertBonusSignalDialog from "./ExpertBonusSignalDialog.vue";
import {
  addExpertConfidenceSignal,
  listConfidenceSignalGroups,
  listExpertBonusSignalPresets,
  listExpertBonusSignals,
  listExpertConfidenceSignals,
  removeExpertBonusSignal,
  removeExpertConfidenceSignal,
  updateExpertBonusSignal,
  updateExpertConfidenceSignal,
  type ConfidenceSignalStatus,
  type ExpertBonusSignal,
  type ExpertConfidenceSignal,
} from "@/app/services/confidenceSignalsApi.service";
import {
  buildExpertSignalSummary,
  groupExpertSignals,
  sortExpertBonusSignals,
  type ExpertSignalGroupView,
} from "@/app/utils/confidenceSignalPresentation";

const props = defineProps<{
  expert: AdminExpertDetail;
}>();

const toaster = useToaster();
const queryClient = useQueryClient();
const pendingSignalKey = ref<string | null>(null);
const pendingBonusId = ref<number | null>(null);
const removeTarget = ref<ExpertConfidenceSignal | null>(null);
const removeBonusTarget = ref<ExpertBonusSignal | null>(null);
const isBonusDialogOpen = ref(false);
const editingBonusSignal = ref<ExpertBonusSignal | null>(null);

const assignmentQueryKey = computed(() => [
  "admin",
  "expert",
  props.expert.id,
  "confidence-signals",
]);

const bonusQueryKey = computed(() => ["admin", "expert", props.expert.id, "bonus-signals"]);

const presetsQueryKey = computed(() => [
  "admin",
  "expert",
  props.expert.id,
  "bonus-signal-presets",
]);

const {
  data: signals,
  isLoading: isLoadingSignals,
  error: signalsError,
  refetch: refetchSignals,
} = useQuery({
  queryKey: assignmentQueryKey,
  queryFn: () => listExpertConfidenceSignals(props.expert.id),
  staleTime: 30_000,
});

const {
  data: bonusSignals,
  isLoading: isLoadingBonusSignals,
  error: bonusSignalsError,
} = useQuery({
  queryKey: bonusQueryKey,
  queryFn: () => listExpertBonusSignals(props.expert.id),
  staleTime: 30_000,
});

const { data: bonusPresets, error: bonusPresetsError } = useQuery({
  queryKey: presetsQueryKey,
  queryFn: () => listExpertBonusSignalPresets(props.expert.id),
  staleTime: 30_000,
});

const { data: groupsResult, isLoading: isLoadingGroups } = useQuery({
  queryKey: ["admin", "confidence-signal-groups"],
  queryFn: listConfidenceSignalGroups,
  staleTime: 0,
});

const groups = computed(() => groupsResult.value?.groups ?? []);

const groupOptions = computed(() =>
  [...groups.value]
    .sort((a, b) => a.sort_index - b.sort_index || a.key.localeCompare(b.key))
    .map((group) => ({ label: group.label, value: group.key })),
);

const isLoading = computed(
  () => isLoadingSignals.value || isLoadingGroups.value || isLoadingBonusSignals.value,
);

const summary = computed(() =>
  buildExpertSignalSummary(signals.value ?? [], bonusSignals.value ?? []),
);

const groupedSignals = computed(() =>
  groupExpertSignals(signals.value ?? [], groups.value, bonusSignals.value ?? []),
);

/**
 * A failed bonus fetch would otherwise just make attached bonus signals vanish
 * from the groups, and a failed preset fetch would render the dialog's "no
 * presets apply" empty state — both look like real states rather than errors.
 */
const bonusLoadError = computed(() =>
  Boolean(bonusSignalsError.value) || Boolean(bonusPresetsError.value),
);

async function retryBonusQueries() {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: bonusQueryKey.value }),
    queryClient.invalidateQueries({ queryKey: presetsQueryKey.value }),
  ]);
}

function isEarning(signal: ExpertConfidenceSignal) {
  return signal.status === "enabled" && signal.has_signal && signal.expert_status === "enabled";
}

function bonusLabel(bonusSignal: ExpertBonusSignal) {
  return bonusSignal.name ?? bonusSignal.public_text;
}

/**
 * Why a bonus is not scoring. `applied` already accounts for both eligibility
 * and the group ceiling, so this only has to explain which of the two it is.
 */
function bonusStateLabel(bonusSignal: ExpertBonusSignal) {
  if (bonusSignal.applied) return "Applied";
  if (bonusSignal.status === "disabled") return "Disabled for this expert";
  if (bonusSignal.source === "preset") return "Not applied — preset disabled, out of scope, or over the group ceiling";
  return "Not applied — over the group ceiling";
}

async function refresh() {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: assignmentQueryKey.value }),
    queryClient.invalidateQueries({ queryKey: bonusQueryKey.value }),
    queryClient.invalidateQueries({ queryKey: presetsQueryKey.value }),
  ]);
}

async function addSignal(signal: ExpertConfidenceSignal) {
  pendingSignalKey.value = signal.key;
  try {
    await addExpertConfidenceSignal(props.expert.id, signal.key);
    await refresh();
    toaster.create({ title: `${signal.name} added to ${props.expert.display_name}`, type: "success" });
  } catch (error) {
    console.error("[ConfidenceSignals] Failed to add expert signal", error);
  } finally {
    pendingSignalKey.value = null;
  }
}

/**
 * The signal's own text first, then its variants. The empty value is the main
 * label rather than "no choice": clearing is what moves an expert back to it.
 */
function labelOptions(signal: ExpertConfidenceSignal) {
  return [
    { label: `Main label — ${signal.public_text}`, value: "" },
    ...signal.label_variants.map((variant) => ({ label: variant.label, value: variant.key })),
  ];
}

async function setSignalLabelVariant(
  signal: ExpertConfidenceSignal,
  value: string | string[] | undefined,
) {
  if (typeof value !== "string") return;

  const next = value === "" ? null : value;
  if (next === signal.label_variant_key) return;

  pendingSignalKey.value = signal.key;
  try {
    // Sent without `status`: choosing what a signal is called says nothing
    // about whether it was earned, so the assignment stays automation-owned.
    await updateExpertConfidenceSignal(props.expert.id, signal.key, { label_variant_key: next });
    await refresh();
    const variant = signal.label_variants.find((item) => item.key === next);
    toaster.create({
      title: variant
        ? `${signal.name} now reads "${variant.label}"`
        : `${signal.name} back to its main label`,
      type: "success",
    });
  } catch (error) {
    console.error("[ConfidenceSignals] Failed to change expert signal label", error);
  } finally {
    pendingSignalKey.value = null;
  }
}

async function setSignalStatus(signal: ExpertConfidenceSignal, status: ConfidenceSignalStatus) {
  pendingSignalKey.value = signal.key;
  try {
    await updateExpertConfidenceSignal(props.expert.id, signal.key, { status });
    await refresh();
    toaster.create({
      title: `${signal.name} ${status === "enabled" ? "enabled" : "disabled"} for this expert`,
      type: "success",
    });
  } catch (error) {
    console.error("[ConfidenceSignals] Failed to update expert signal", error);
  } finally {
    pendingSignalKey.value = null;
  }
}

async function confirmRemove() {
  const signal = removeTarget.value;
  if (!signal) return;

  pendingSignalKey.value = signal.key;
  try {
    await removeExpertConfidenceSignal(props.expert.id, signal.key);
    await refresh();
    toaster.create({ title: `${signal.name} removed from this expert`, type: "success" });
    removeTarget.value = null;
  } catch (error) {
    console.error("[ConfidenceSignals] Failed to remove expert signal", error);
  } finally {
    pendingSignalKey.value = null;
  }
}

function openAddBonusSignal() {
  editingBonusSignal.value = null;
  isBonusDialogOpen.value = true;
}

function openEditBonusSignal(bonusSignal: ExpertBonusSignal) {
  editingBonusSignal.value = bonusSignal;
  isBonusDialogOpen.value = true;
}

async function setBonusStatus(bonusSignal: ExpertBonusSignal, status: ConfidenceSignalStatus) {
  pendingBonusId.value = bonusSignal.id;
  try {
    await updateExpertBonusSignal(props.expert.id, bonusSignal.id, { status });
    await refresh();
    toaster.create({
      title: `${bonusLabel(bonusSignal)} ${status === "enabled" ? "enabled" : "disabled"}`,
      type: "success",
    });
  } catch (error) {
    console.error("[BonusSignals] Failed to update bonus signal status", error);
  } finally {
    pendingBonusId.value = null;
  }
}

function canMoveBonusSignal(
  group: ExpertSignalGroupView,
  bonusSignal: ExpertBonusSignal,
  direction: "up" | "down",
) {
  const ordered = sortExpertBonusSignals(group.bonusSignals);
  const index = ordered.findIndex((item) => item.id === bonusSignal.id);
  const target = index + (direction === "up" ? -1 : 1);
  return index >= 0 && target >= 0 && target < ordered.length;
}

/**
 * Reassigns the whole group sequentially rather than swapping two values:
 * bonus signals default to sort_index 0, so a plain swap of equal indices
 * would leave the order unchanged.
 */
async function moveBonusSignal(
  group: ExpertSignalGroupView,
  bonusSignal: ExpertBonusSignal,
  direction: "up" | "down",
) {
  if (!canMoveBonusSignal(group, bonusSignal, direction) || pendingBonusId.value !== null) return;

  const ordered = sortExpertBonusSignals(group.bonusSignals);
  const index = ordered.findIndex((item) => item.id === bonusSignal.id);
  const target = index + (direction === "up" ? -1 : 1);
  const [moved] = ordered.splice(index, 1);
  ordered.splice(target, 0, moved!);

  const changed = ordered
    .map((item, position) => ({ item, position }))
    .filter(({ item, position }) => item.sort_index !== position);

  pendingBonusId.value = bonusSignal.id;
  try {
    await Promise.all(
      changed.map(({ item, position }) =>
        updateExpertBonusSignal(props.expert.id, item.id, { sort_index: position }),
      ),
    );
    await refresh();
    toaster.create({ title: "Bonus signal order updated", type: "success" });
  } catch (error) {
    console.error("[BonusSignals] Failed to reorder bonus signals", error);
    await refresh();
  } finally {
    pendingBonusId.value = null;
  }
}

async function confirmRemoveBonusSignal() {
  const bonusSignal = removeBonusTarget.value;
  if (!bonusSignal) return;

  pendingBonusId.value = bonusSignal.id;
  try {
    await removeExpertBonusSignal(props.expert.id, bonusSignal.id);
    await refresh();
    toaster.create({ title: `${bonusLabel(bonusSignal)} removed`, type: "success" });
    removeBonusTarget.value = null;
  } catch (error) {
    console.error("[BonusSignals] Failed to remove bonus signal", error);
  } finally {
    pendingBonusId.value = null;
  }
}
</script>

<template>
  <div class="flex flex-col gap-6">
    <div class="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
      <div class="max-w-2xl">
        <div class="mb-2 flex items-center gap-2 text-emerald-700">
          <UiIcon name="shield-tick" class="h-5 w-5" />
          <UiText as="span" variant="caption" class="font-semibold uppercase tracking-[0.12em]">
            Expert trust profile
          </UiText>
        </div>
        <UiText as="h2" variant="h5" class="font-semibold text-gray-900">Trust Signals</UiText>
        <UiText as="p" variant="p-small" class="mt-1 text-gray-500">
          Add or remove signals, or disable an assigned signal only for this expert. Global visibility remains controlled from Trust Signals settings.
        </UiText>
      </div>

      <div class="flex flex-col items-end gap-3">
        <div class="flex overflow-hidden rounded-lg border border-gray-200 bg-gray-200">
          <div class="min-w-28 bg-white px-4 py-3">
            <p class="text-[10px] font-semibold uppercase tracking-wider text-gray-400">Earned</p>
            <p class="mt-0.5 text-xl font-semibold tabular-nums text-emerald-700">{{ summary.totalPoints }}</p>
          </div>
          <div class="min-w-28 bg-white px-4 py-3">
            <p class="text-[10px] font-semibold uppercase tracking-wider text-gray-400">Bonus</p>
            <p class="mt-0.5 text-xl font-semibold tabular-nums text-amber-600">{{ summary.bonusPoints }}</p>
          </div>
          <div class="min-w-28 bg-gray-950 px-4 py-3 text-white">
            <p class="text-[10px] font-semibold uppercase tracking-wider text-gray-400">Available</p>
            <p class="mt-0.5 text-xl font-semibold tabular-nums">{{ summary.availablePoints }}</p>
          </div>
        </div>

        <UiButton variant="outline" label="Add bonus signal" @click="openAddBonusSignal">
          <template #before_label><UiIcon name="star" class="h-4 w-4" /></template>
        </UiButton>
      </div>
    </div>

    <div v-if="isLoading" class="rounded-lg border border-gray-100 py-14 text-center">
      <div class="mx-auto mb-3 h-7 w-7 animate-spin rounded-full border-2 border-gray-200 border-t-emerald-600" />
      <UiText as="p" variant="p-small" class="text-gray-500">Loading expert trust signals...</UiText>
    </div>

    <div v-else-if="signalsError" class="rounded-lg border border-red-200 bg-red-50 p-5 text-center">
      <UiText as="p" variant="p-small" class="text-red-700">Trust signals could not be loaded.</UiText>
      <UiButton class="mt-3" variant="outline" label="Try again" @click="refetchSignals()" />
    </div>

    <div v-else class="space-y-5">
      <div
        v-if="bonusLoadError"
        class="flex flex-col gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4 sm:flex-row sm:items-center sm:justify-between"
      >
        <UiText as="p" variant="p-small" class="text-amber-900">
          Bonus signals could not be loaded, so any attached to this expert are not shown here and
          the preset list will look empty. Points shown below exclude them.
        </UiText>
        <UiButton variant="outline" label="Try again" @click="retryBonusQueries" />
      </div>

      <section
        v-for="group in groupedSignals"
        :key="group.key"
        class="overflow-hidden rounded-lg border border-gray-200"
      >
        <div class="flex flex-col gap-2 border-b border-gray-200 bg-gray-50/70 px-5 py-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <UiText as="h3" variant="p" class="font-semibold text-gray-900">{{ group.label }}</UiText>
            <p class="mt-0.5 font-mono text-[11px] text-gray-400">{{ group.key }}</p>
          </div>
          <div class="flex items-center gap-4 text-xs text-gray-500">
            <span><strong class="font-semibold text-gray-800">{{ group.earningCount }}</strong> earning</span>
            <span v-if="group.bonusPoints > 0">
              <strong class="font-semibold text-amber-600">+{{ group.bonusPoints }}</strong> bonus
            </span>
            <span><strong class="font-semibold text-emerald-700">{{ group.totalPoints }}</strong> / {{ group.availablePoints }} pts</span>
          </div>
        </div>

        <div class="divide-y divide-gray-100">
          <article
            v-for="signal in group.signals"
            :key="signal.key"
            class="flex flex-col gap-4 px-5 py-4 lg:flex-row lg:items-center lg:justify-between"
            :class="signal.status === 'disabled' ? 'bg-gray-50/50' : 'bg-white'"
          >
            <div class="flex min-w-0 items-start gap-3">
              <div
                class="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full"
                :class="isEarning(signal) ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-400'"
              >
                <UiIcon :name="isEarning(signal) ? 'check' : 'circle'" class="h-4 w-4" />
              </div>
              <div class="min-w-0">
                <div class="flex flex-wrap items-center gap-2">
                  <UiText as="p" variant="p-small" class="font-semibold text-gray-900">{{ signal.name }}</UiText>
                  <span class="rounded bg-gray-100 px-1.5 py-0.5 font-mono text-[10px] text-gray-500">{{ signal.key }}</span>
                  <span
                    v-if="signal.status === 'disabled'"
                    class="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-800"
                  >Globally hidden</span>
                  <span
                    v-if="signal.label_variant_key"
                    class="rounded bg-sky-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-sky-800"
                  >Variant</span>
                </div>
                <UiText as="p" variant="p-small" class="mt-1 text-gray-500">
                  {{ signal.effective_public_text }}
                </UiText>
                <div class="mt-1.5 flex items-center gap-3 text-xs">
                  <span class="font-semibold text-emerald-700">+{{ signal.points }} pts</span>
                  <span v-if="signal.has_signal" :class="signal.expert_status === 'enabled' ? 'text-gray-500' : 'text-amber-700'">
                    {{ signal.expert_status === 'enabled' ? 'Assigned and enabled' : 'Assigned but disabled for expert' }}
                  </span>
                  <span v-else class="text-gray-400">Not assigned</span>
                </div>

                <div
                  v-if="signal.has_signal && signal.label_variants.length > 0"
                  class="mt-2.5 max-w-sm"
                >
                  <label
                    :for="`${signal.key}-label-variant`"
                    class="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-gray-400"
                  >Published wording</label>
                  <UiSelect
                    :model-value="signal.label_variant_key ?? ''"
                    :name="`${signal.key}-label-variant`"
                    :options="labelOptions(signal)"
                    :disabled="pendingSignalKey === signal.key"
                    @update:model-value="setSignalLabelVariant(signal, $event)"
                  />
                  <p class="mt-1 text-[11px] text-gray-400">
                    Wording only — points, source, and scope are unchanged.
                  </p>
                </div>
                <p
                  v-else-if="signal.label_variants.length > 0"
                  class="mt-2 text-[11px] text-gray-400"
                >
                  {{ signal.label_variants.length }} label variant{{ signal.label_variants.length === 1 ? '' : 's' }} available — add the signal to pick one.
                </p>
              </div>
            </div>

            <div class="flex shrink-0 items-center gap-2 self-end lg:self-auto">
              <UiButton
                v-if="!signal.has_signal"
                variant="outline"
                :label="pendingSignalKey === signal.key ? 'Adding...' : 'Add signal'"
                :disabled="pendingSignalKey === signal.key"
                @click="addSignal(signal)"
              >
                <template #before_label><UiIcon name="plus" class="h-4 w-4" /></template>
              </UiButton>

              <template v-else>
                <UiButton
                  variant="outline"
                  :label="signal.expert_status === 'enabled' ? 'Disable' : 'Enable'"
                  :disabled="pendingSignalKey === signal.key"
                  @click="setSignalStatus(signal, signal.expert_status === 'enabled' ? 'disabled' : 'enabled')"
                />
                <UiButton
                  variant="ghost"
                  shape="square"
                  :disabled="pendingSignalKey === signal.key"
                  :title="`Remove ${signal.name}`"
                  :aria-label="`Remove ${signal.name}`"
                  @click="removeTarget = signal"
                >
                  <template #before_label><UiIcon name="delete" class="h-4 w-4 text-red-600" /></template>
                </UiButton>
              </template>
            </div>
          </article>

          <article
            v-for="bonusSignal in group.bonusSignals"
            :key="`bonus-${bonusSignal.id}`"
            class="flex flex-col gap-4 px-5 py-4 lg:flex-row lg:items-center lg:justify-between"
            :class="bonusSignal.applied ? 'bg-amber-50/40' : 'bg-gray-50/50'"
          >
            <div class="flex min-w-0 items-start gap-3">
              <div
                class="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full"
                :class="bonusSignal.applied ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-400'"
              >
                <UiIcon name="star" class="h-4 w-4" />
              </div>
              <div class="min-w-0">
                <div class="flex flex-wrap items-center gap-2">
                  <UiText as="p" variant="p-small" class="font-semibold text-gray-900">
                    {{ bonusLabel(bonusSignal) }}
                  </UiText>
                  <span class="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-800">
                    Bonus
                  </span>
                  <span
                    v-if="bonusSignal.source === 'preset'"
                    class="rounded bg-gray-100 px-1.5 py-0.5 font-mono text-[10px] text-gray-500"
                  >{{ bonusSignal.preset_key }}</span>
                </div>
                <UiText as="p" variant="p-small" class="mt-1 text-gray-500">{{ bonusSignal.public_text }}</UiText>
                <div class="mt-1.5 flex flex-wrap items-center gap-3 text-xs">
                  <span class="font-semibold text-amber-700">+{{ bonusSignal.points }} pts</span>
                  <span :class="bonusSignal.applied ? 'text-gray-500' : 'text-amber-700'">
                    {{ bonusStateLabel(bonusSignal) }}
                  </span>
                  <span v-if="bonusSignal.source === 'preset'" class="text-gray-400">
                    Follows its preset — edit it under Trust Signals
                  </span>
                </div>
              </div>
            </div>

            <div class="flex shrink-0 items-center gap-2 self-end lg:self-auto">
              <div class="flex items-center gap-1">
                <UiIconButton
                  icon="chevron-down"
                  variant="ghost"
                  class="h-8 w-8 rotate-180 border border-gray-200"
                  :aria-label="`Move ${bonusLabel(bonusSignal)} up`"
                  :disabled="!canMoveBonusSignal(group, bonusSignal, 'up') || pendingBonusId !== null"
                  @click="moveBonusSignal(group, bonusSignal, 'up')"
                />
                <UiIconButton
                  icon="chevron-down"
                  variant="ghost"
                  class="h-8 w-8 border border-gray-200"
                  :aria-label="`Move ${bonusLabel(bonusSignal)} down`"
                  :disabled="!canMoveBonusSignal(group, bonusSignal, 'down') || pendingBonusId !== null"
                  @click="moveBonusSignal(group, bonusSignal, 'down')"
                />
              </div>

              <UiButton
                variant="outline"
                :label="bonusSignal.status === 'enabled' ? 'Disable' : 'Enable'"
                :disabled="pendingBonusId === bonusSignal.id"
                @click="setBonusStatus(bonusSignal, bonusSignal.status === 'enabled' ? 'disabled' : 'enabled')"
              />

              <UiButton
                v-if="bonusSignal.source === 'custom'"
                variant="ghost"
                shape="square"
                :title="`Edit ${bonusLabel(bonusSignal)}`"
                :aria-label="`Edit ${bonusLabel(bonusSignal)}`"
                :disabled="pendingBonusId === bonusSignal.id"
                @click="openEditBonusSignal(bonusSignal)"
              >
                <template #before_label><UiIcon name="pencil" class="h-4 w-4 text-gray-600" /></template>
              </UiButton>

              <UiButton
                variant="ghost"
                shape="square"
                :title="`Remove ${bonusLabel(bonusSignal)}`"
                :aria-label="`Remove ${bonusLabel(bonusSignal)}`"
                :disabled="pendingBonusId === bonusSignal.id"
                @click="removeBonusTarget = bonusSignal"
              >
                <template #before_label><UiIcon name="delete" class="h-4 w-4 text-red-600" /></template>
              </UiButton>
            </div>
          </article>
        </div>
      </section>
    </div>

    <ExpertBonusSignalDialog
      v-model="isBonusDialogOpen"
      :expert-id="expert.id"
      :expert-name="expert.display_name"
      :group-options="groupOptions"
      :presets="bonusPresets ?? []"
      :editing="editingBonusSignal"
      @saved="refresh"
    />

    <UiDialog
      :model-value="Boolean(removeTarget)"
      title="Remove trust signal?"
      :text="removeTarget ? `${removeTarget.name} will no longer be assigned to ${expert.display_name}.` : ''"
      primary-action-label="Remove signal"
      secondary-action-label="Cancel"
      size="sm"
      :loading="Boolean(removeTarget && pendingSignalKey === removeTarget.key)"
      @update:model-value="(open) => { if (!open) removeTarget = null }"
      @primary-action="confirmRemove"
      @secondary-action="removeTarget = null"
    />

    <UiDialog
      :model-value="Boolean(removeBonusTarget)"
      title="Remove bonus signal?"
      :text="removeBonusTarget ? `${bonusLabel(removeBonusTarget)} will no longer be attached to ${expert.display_name}.` : ''"
      primary-action-label="Remove bonus signal"
      secondary-action-label="Cancel"
      size="sm"
      :loading="Boolean(removeBonusTarget && pendingBonusId === removeBonusTarget.id)"
      @update:model-value="(open) => { if (!open) removeBonusTarget = null }"
      @primary-action="confirmRemoveBonusSignal"
      @secondary-action="removeBonusTarget = null"
    />
  </div>
</template>
